import { useCallback, useMemo, useRef } from 'react';
import { Switch } from 'antd';

import AxisConfigDropdown from 'components/molecules/PackArchitecture/AxisConfigDropdown';
import type { AxisOptionGroup } from 'components/molecules/PackArchitecture/AxisConfigDropdown';
import { colorConstants } from 'config/colorConstants';
import {
    PPA_PACK_ARCH_AXIS_X,
    PPA_PACK_ARCH_AXIS_X_SETTINGS,
    PPA_PACK_ARCH_AXIS_Y,
    PPA_PACK_ARCH_BUBBLE_REPRESENTS,
    PPA_PACK_ARCH_BUBBLE_SIZE,
    PPA_PACK_ARCH_HEATMAP_TOGGLE,
    PPA_PACK_ARCH_TOOLBAR,
} from 'constants/ppaTestIds';
import { usePackArchitectureContext } from 'contexts/PackArchitectureContext';
import { useGetCurrencyForTenant } from 'hooks/general/useCurrencyForClient';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PPA_PACK_ARCH_CHART_CONFIG_CHANGED,
    PPA_PACK_ARCH_HEATMAP_ENABLED,
} from 'utils/mixpanelEventNames';

import {
    getAxisLabel,
    getKpiBinLabelAffixesForXAxis,
    getPackArchitectureComparisonPeriodLabel,
    getXAxisKpiOptionIds,
} from './packArchitectureMeta.helpers';

type ChartConfigSnapshot = {
    xAxis: string;
    yAxis: string;
    bubbleRepresents: string;
    bubbleSize: string;
};

type ChartConfigLabels = {
    xAxisOptionGroups: AxisOptionGroup[];
    yAxisOptionGroups: AxisOptionGroup[];
    bubbleRepresentsOptionGroups: AxisOptionGroup[];
    bubbleSizeOptionGroups: AxisOptionGroup[];
};

const trackChartConfigChanged = (
    previous: ChartConfigSnapshot,
    selected: ChartConfigSnapshot,
    labels: ChartConfigLabels,
) => {
    trackEventMixpanel(PPA_PACK_ARCH_CHART_CONFIG_CHANGED, {
        previous_x_axis: getAxisLabel(previous.xAxis, labels.xAxisOptionGroups),
        selected_x_axis: getAxisLabel(selected.xAxis, labels.xAxisOptionGroups),
        previous_y_axis: getAxisLabel(previous.yAxis, labels.yAxisOptionGroups),
        selected_y_axis: getAxisLabel(selected.yAxis, labels.yAxisOptionGroups),
        previous_bubble_represents: getAxisLabel(
            previous.bubbleRepresents,
            labels.bubbleRepresentsOptionGroups,
        ),
        selected_bubble_represents: getAxisLabel(
            selected.bubbleRepresents,
            labels.bubbleRepresentsOptionGroups,
        ),
        previous_bubble_size: getAxisLabel(previous.bubbleSize, labels.bubbleSizeOptionGroups),
        selected_bubble_size: getAxisLabel(selected.bubbleSize, labels.bubbleSizeOptionGroups),
    });
};

/**
 * Reads all X-axis settings from context directly — no prop drilling needed.
 * Kept private to this module; only used by PackArchitectureToolbar.
 */
const XAxisConfigDropdown = ({
    onXAxisChange,
}: {
    onXAxisChange: (value: string) => void;
}) => {
    const {
        xAxis,
        xAxisConfig,
        xAxisOptionGroups,
        xAxisGroupByControlsMeta,
        xAxisKpiControlsMeta,
        xAxisKpiDataRange,
        xAxisDimensionConfig,
        setXAxisDimensionConfig,
        xAxisKpiConfig,
        setXAxisKpiConfig,
        chartRequest,
        totalCount,
        isChartLoading,
    } = usePackArchitectureContext();

    const currencyPrefix = useGetCurrencyForTenant();

    const kpiBinLabelAffixes = useMemo(
        () => getKpiBinLabelAffixesForXAxis(xAxis, xAxisConfig, currencyPrefix),
        [currencyPrefix, xAxis, xAxisConfig],
    );

    const xAxisKpiOptionIds = useMemo(() => getXAxisKpiOptionIds(xAxisConfig), [xAxisConfig]);

    return (
        <AxisConfigDropdown
            label={xAxisConfig?.label ?? 'X-Axis'}
            value={xAxis}
            onChange={onXAxisChange}
            optionGroups={xAxisOptionGroups}
            kpiColumnOptionIds={xAxisKpiOptionIds}
            showSettings
            groupByControlsMeta={xAxisGroupByControlsMeta}
            kpiControlsMeta={xAxisKpiControlsMeta}
            chartRequest={chartRequest}
            apiTotalCount={totalCount}
            kpiDataRange={xAxisKpiDataRange}
            kpiBinLabelAffixes={kpiBinLabelAffixes}
            axisConfiguration={xAxisDimensionConfig}
            onAxisConfigurationChange={setXAxisDimensionConfig}
            kpiAxisConfiguration={xAxisKpiConfig}
            onKpiAxisConfigurationChange={setXAxisKpiConfig}
            triggerTestId={PPA_PACK_ARCH_AXIS_X}
            settingsTestId={PPA_PACK_ARCH_AXIS_X_SETTINGS}
            isLoading={isChartLoading}
        />
    );
};

const PackArchitectureToolbar = () => {
    const {
        xAxis,
        setXAxis,
        xAxisOptionGroups,
        yAxis,
        setYAxis,
        yAxisConfig,
        yAxisOptionGroups,
        bubbleRepresents,
        setBubbleRepresents,
        bubbleRepresentsConfig,
        bubbleRepresentsOptionGroups,
        bubbleSize,
        setBubbleSize,
        bubbleSizeConfig,
        bubbleSizeOptionGroups,
        showHeatMap,
        setShowHeatMap,
        heatMapToggle,
    } = usePackArchitectureContext();

    const labelGroupsRef = useRef<ChartConfigLabels>({
        xAxisOptionGroups,
        yAxisOptionGroups,
        bubbleRepresentsOptionGroups,
        bubbleSizeOptionGroups,
    });
    labelGroupsRef.current = {
        xAxisOptionGroups,
        yAxisOptionGroups,
        bubbleRepresentsOptionGroups,
        bubbleSizeOptionGroups,
    };

    const handleXAxisChange = useCallback(
        (value: string) => {
            if (value !== xAxis) {
                trackChartConfigChanged(
                    { xAxis, yAxis, bubbleRepresents, bubbleSize },
                    { xAxis: value, yAxis, bubbleRepresents, bubbleSize },
                    labelGroupsRef.current,
                );
            }
            setXAxis(value);
        },
        [bubbleRepresents, bubbleSize, setXAxis, xAxis, yAxis],
    );

    const handleYAxisChange = useCallback(
        (value: string) => {
            if (value !== yAxis) {
                trackChartConfigChanged(
                    { xAxis, yAxis, bubbleRepresents, bubbleSize },
                    { xAxis, yAxis: value, bubbleRepresents, bubbleSize },
                    labelGroupsRef.current,
                );
            }
            setYAxis(value);
        },
        [bubbleRepresents, bubbleSize, setYAxis, xAxis, yAxis],
    );

    const handleBubbleRepresentsChange = useCallback(
        (value: string) => {
            if (value !== bubbleRepresents) {
                trackChartConfigChanged(
                    { xAxis, yAxis, bubbleRepresents, bubbleSize },
                    { xAxis, yAxis, bubbleRepresents: value, bubbleSize },
                    labelGroupsRef.current,
                );
            }
            setBubbleRepresents(value);
        },
        [bubbleRepresents, bubbleSize, setBubbleRepresents, xAxis, yAxis],
    );

    const handleBubbleSizeChange = useCallback(
        (value: string) => {
            if (value !== bubbleSize) {
                trackChartConfigChanged(
                    { xAxis, yAxis, bubbleRepresents, bubbleSize },
                    { xAxis, yAxis, bubbleRepresents, bubbleSize: value },
                    labelGroupsRef.current,
                );
            }
            setBubbleSize(value);
        },
        [bubbleRepresents, bubbleSize, setBubbleSize, xAxis, yAxis],
    );

    const handleHeatMapChange = useCallback(
        (checked: boolean) => {
            if (checked) {
                trackEventMixpanel(PPA_PACK_ARCH_HEATMAP_ENABLED, {
                    bubble_size_metric: getAxisLabel(bubbleSize, bubbleSizeOptionGroups),
                    comparison_period: getPackArchitectureComparisonPeriodLabel({
                        heatMapToggleLabel: heatMapToggle?.label,
                    }),
                });
            }
            setShowHeatMap(checked);
        },
        [bubbleSize, bubbleSizeOptionGroups, heatMapToggle?.label, setShowHeatMap],
    );

    const label = useMemo(() => {
        if (!bubbleSizeOptionGroups || bubbleSizeOptionGroups?.length === 0 || !bubbleSizeOptionGroups[0].options)
            return '';
        return bubbleSizeOptionGroups[0].options?.find((option: any) => option.value === bubbleSize)?.label || '';
    }, [bubbleSize, bubbleSizeOptionGroups]);

    return (
        <div className="mb-4 flex flex-wrap items-center gap-4" data-testid={PPA_PACK_ARCH_TOOLBAR}>
            <div className="flex items-center gap-4">
                <XAxisConfigDropdown onXAxisChange={handleXAxisChange} />
                <AxisConfigDropdown
                    label={yAxisConfig?.label ?? 'Y-Axis'}
                    value={yAxis}
                    onChange={handleYAxisChange}
                    optionGroups={yAxisOptionGroups}
                    triggerTestId={PPA_PACK_ARCH_AXIS_Y}
                />
            </div>

            <div className="h-8 w-px shrink-0 bg-borderGreyAl1" role="separator" aria-orientation="vertical" />

            <div className="pack-architecture-combined-bubble-config inline-flex h-8 items-stretch rounded-md bg-white">
                <div className="relative -mr-px flex h-8 items-stretch rounded-l-md border border-borderGreyAl1 hover:z-10 hover:border-purpleFontSelected">
                    <AxisConfigDropdown
                        embedded
                        label={bubbleRepresentsConfig?.label ?? 'Bubble Represents'}
                        value={bubbleRepresents}
                        onChange={handleBubbleRepresentsChange}
                        optionGroups={bubbleRepresentsOptionGroups}
                        triggerTestId={PPA_PACK_ARCH_BUBBLE_REPRESENTS}
                    />
                </div>
                <div className="relative flex h-8 items-stretch rounded-r-md border border-borderGreyAl1 hover:z-10 hover:border-purpleFontSelected">
                    <AxisConfigDropdown
                        embedded
                        label={bubbleSizeConfig?.label ?? 'Size'}
                        value={bubbleSize}
                        onChange={handleBubbleSizeChange}
                        optionGroups={bubbleSizeOptionGroups}
                        triggerTestId={PPA_PACK_ARCH_BUBBLE_SIZE}
                    />
                </div>
            </div>

            {heatMapToggle ? (
                <div
                    className="ml-auto flex items-center gap-2"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                >
                    <Switch
                        size="small"
                        checked={showHeatMap}
                        onChange={handleHeatMapChange}
                        data-testid={PPA_PACK_ARCH_HEATMAP_TOGGLE}
                    />
                    <span className="text-[14px]" style={{ color: colorConstants.blackTransparent88 }}>
                        {heatMapToggle.label} : {label}
                    </span>
                </div>
            ) : null}
        </div>
    );
};

export default PackArchitectureToolbar;
